import type {
	CustomMetricData,
	DatadogConfig,
	DatadogError,
	ErrorCategory
} from '../app/core/ports/datadog';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum-slim';

import { MetricActionName } from '../app/core/ports/datadog';

/**
 * DATADOG CUSTOM VITALS - USAGE EXAMPLES
 *
 * The Custom Vitals feature allows you to measure component-level performance in your application.
 * Here are practical examples for the real estate portal:
 *
 * 1. COMPONENT EXAMPLE - Property Card Component:
 * ```vue
 * <template>
 *   <div class="property-card">
 *     <img @load="handleImageLoad" :src="property.image" />
 *     <div class="property-info">{{ property.title }}</div>
 *   </div>
 * </template>
 *
 * <script setup lang="ts">
 * const { trackComponentRender, trackPropertyImageLoad } = useDatadog();
 * const { trackPropertyPageLoad } = usePortalPerformance();
 *
 * // Track component rendering time
 * const stopRenderTracking = trackComponentRender('PropertyCard');
 *
 * // Track when component is fully mounted
 * onMounted(() => {
 *   stopRenderTracking();
 * });
 *
 * // Track individual image loading
 * function handleImageLoad() {
 *   const imageVitalRef = trackPropertyImageLoad(props.property.id);
 *   if (imageVitalRef) {
 *     // Image loaded, stop tracking
 *     // This is automatically handled by the trackPropertyImageLoad function
 *   }
 * }
 * </script>
 * ```
 *
 * 2. PAGE EXAMPLE - Property Detail Page:
 * ```vue
 * <template>
 *   <div>
 *     <PropertyHeader :property="property" />
 *     <PropertyGallery :images="property.images" />
 *     <PropertyDetails :property="property" />
 *   </div>
 * </template>
 *
 * <script setup lang="ts">
 * const route = useRoute();
 * const { trackPropertyPageLoad } = usePortalPerformance();
 * const { addCustomVital } = useDatadog();
 *
 * // Track page load performance
 * const pageLoadStart = performance.now();
 * let pageVitalRef: string | void;
 *
 * onMounted(() => {
 *   pageVitalRef = trackPropertyPageLoad(route.params.id as string);
 * });
 *
 * // When all data is loaded and page is interactive
 * watch([property, isLoading], ([newProperty, newIsLoading]) => {
 *   if (newProperty && !newIsLoading && pageVitalRef) {
 *     // Page is fully loaded
 *     addCustomVital('property_page_complete', {
 *       startTime: pageLoadStart,
 *       duration: performance.now() - pageLoadStart,
 *       description: 'Complete property page load time',
 *       context: {
 *         property_id: newProperty.id,
 *         property_type: newProperty.type,
 *         has_gallery: newProperty.images?.length > 0
 *       }
 *     });
 *   }
 * });
 * </script>
 * ```
 *
 * 3. API CALL EXAMPLE - Property Search:
 * ```vue
 * <script setup lang="ts">
 * const { trackAsyncOperation } = useDatadog();
 * const { trackSearchPerformance } = usePortalPerformance();
 *
 * async function searchProperties(query: string) {
 *   // Track the async operation
 *   const stopAsyncTracking = trackAsyncOperation('property_search_api');
 *
 *   // Track search-specific performance
 *   const searchVitalRef = trackSearchPerformance(query);
 *
 *   try {
 *     const response = await $fetch('/api/properties/search', {
 *       method: 'POST',
 *       body: { query, filters: currentFilters.value }
 *     });
 *
 *     // Stop tracking when done
 *     stopAsyncTracking();
 *     if (searchVitalRef) {
 *       // Search completed successfully
 *       addCustomVital('search_results_display', {
 *         duration: performance.now() - searchStartTime,
 *         description: 'Time to display search results',
 *         context: {
 *           query,
 *           results_count: response.results.length,
 *           has_filters: Object.keys(currentFilters.value).length > 0
 *         }
 *       });
 *     }
 *
 *     return response;
 *   } catch (error) {
 *     stopAsyncTracking();
 *     throw error;
 *   }
 * }
 * </script>
 * ```
 *
 * 4. FORM SUBMISSION EXAMPLE:
 * ```vue
 * <script setup lang="ts">
 * const { trackFormSubmission } = usePortalPerformance();
 *
 * async function submitContactForm(formData: ContactForm) {
 *   const formVitalRef = trackFormSubmission('property_contact');
 *
 *   try {
 *     await $fetch('/api/contact', {
 *       method: 'POST',
 *       body: formData
 *     });
 *
 *     // Form submitted successfully
 *     if (formVitalRef) {
 *       addCustomVital('form_success', {
 *         duration: performance.now() - formStartTime,
 *         description: 'Successful form submission',
 *         context: {
 *           form_type: 'property_contact',
 *           property_id: formData.propertyId,
 *           user_type: user.value ? 'authenticated' : 'anonymous'
 *         }
 *       });
 *     }
 *   } catch (error) {
 *     // Handle error
 *     trackError({
 *       message: 'Form submission failed',
 *       source: 'user',
 *       category: 'form_error',
 *       stack: error.stack
 *     });
 *   }
 * }
 * </script>
 * ```
 *
 * 5. QUERY IN DATADOG:
 * After implementing Custom Vitals, you can query them in Datadog using:
 * - `@vital.name:property_page_load` - Filter by vital name
 * - `@vital.duration:>1000` - Filter by duration (in milliseconds)
 * - `@vital.description:"Time to load complete property page"` - Filter by description
 * - `@vital.property_id:123` - Filter by custom context
 *
 * IMPORTANT NOTES:
 * - Custom Vitals require experimental features to be enabled
 * - Set NUXT_PUBLIC_DATADOG_ENABLE_EXPERIMENTAL_FEATURES="custom-vitals" in your environment
 * - Custom vital names cannot contain spaces or special characters
 * - Use the `description` field for human-readable descriptions
 * - Use the `context` field for additional metadata and filtering
 */
interface UseDatadogReturn {
	trackCustomAction: (
		actionName: MetricActionName,
		additionalData?: CustomMetricData
	) => void;
	trackError: (error: DatadogError) => void;
	setUser: (user: { id: string; name?: string; email?: string }) => void;
	addContext: (key: string, value: any) => void;
	startView: (name: string, context?: Record<string, any>) => void;
	isInitialized: () => boolean;
	// Portal-specific methods
	trackPropertyEvent: (
		event: string,
		propertyId?: string,
		additionalContext?: Record<string, any>
	) => void;
	trackSearchEvent: (
		searchQuery: string,
		resultsCount?: number,
		filters?: any
	) => void;
	trackLeadEvent: (
		leadType: string,
		propertyId?: string,
		formData?: any
	) => void;
	trackFeatureUsage: (
		featureName: string,
		context?: Record<string, any>
	) => void;
	trackWebVital: (
		name: string,
		value: number,
		rating: 'good' | 'needs-improvement' | 'poor'
	) => void;
	updateUser: (userData: Record<string, any>) => void;
	startSessionReplay: () => void;
	stopSessionReplay: () => void;
	// Custom Vitals methods
	startCustomVital: (
		name: string,
		options?: { description?: string; context?: Record<string, any> }
	) => string | void;
	stopCustomVital: (nameOrReference: string) => void;
	addCustomVital: (
		name: string,
		options: {
			startTime?: number;
			duration: number;
			description?: string;
			context?: Record<string, any>;
		}
	) => void;
	// Component performance tracking
	trackComponentRender: (componentName: string) => () => void;
	trackAsyncOperation: (operationName: string) => () => void;
}

/**
 * Composable for Datadog RUM and Logs integration
 * Provides complete monitoring of the real estate portal frontend
 * Note: Datadog is initialized by the plugin, this composable only provides utility functions
 */
export function useDatadog(): UseDatadogReturn {
	/**
	 * Checks if Datadog is initialized by trying to access RUM
	 */
	function isInitialized(): boolean {
		if (import.meta.server) return false;
		try {
			return (
				typeof window !== 'undefined' &&
				typeof window.DD_RUM !== 'undefined' &&
				typeof (window.DD_RUM as any).startDurationVital === 'function'
			);
		} catch {
			return false;
		}
	}

	/**
	 * Checks if Custom Vitals APIs are available (experimental feature)
	 */
	function isCustomVitalsAvailable(): boolean {
		return (
			isInitialized() &&
			typeof (window.DD_RUM as any).startDurationVital === 'function' &&
			typeof (window.DD_RUM as any).stopDurationVital === 'function' &&
			typeof (window.DD_RUM as any).addDurationVital === 'function'
		);
	}

	/**
	 * Tracks custom user actions
	 */
	function trackCustomAction(
		actionName: MetricActionName,
		additionalData: CustomMetricData = {}
	): void {
		if (!isInitialized()) {
			console.warn('Datadog not initialized, skipping action tracking');
			return;
		}

		try {
			const actionData = {
				timestamp: new Date().toISOString(),
				userAgent: navigator.userAgent,
				url: window.location.href,
				referrer: document.referrer,
				...additionalData
			};

			datadogRum.addAction(actionName, actionData);

			// Complementary log for analysis
			datadogLogs.logger.info(`User action: ${actionName}`, actionData);
		} catch (error) {
			console.error('Error tracking custom action:', error);
		}
	}

	/**
	 * Track property-specific events
	 */
	function trackPropertyEvent(
		event: string,
		propertyId?: string,
		additionalContext?: Record<string, any>
	): void {
		if (!propertyId) return;

		trackCustomAction(MetricActionName.PropertyView, {
			name: event,
			value: 1,
			tags: [`property_id:${propertyId}`, `event:${event}`],
			property_id: propertyId,
			category: 'property',
			...additionalContext
		});
	}

	/**
	 * Track search events
	 */
	function trackSearchEvent(
		searchQuery: string,
		resultsCount?: number,
		filters?: any
	): void {
		trackCustomAction(MetricActionName.PropertySearch, {
			name: 'search_performed',
			value: resultsCount || 0,
			tags: [`query:${searchQuery}`, `results:${resultsCount || 0}`],
			search_query: searchQuery,
			results_count: resultsCount,
			filters: filters,
			category: 'search'
		});
	}

	/**
	 * Track lead generation events
	 */
	function trackLeadEvent(
		leadType: string,
		propertyId?: string,
		formData?: any
	): void {
		trackCustomAction(MetricActionName.PropertyContact, {
			name: 'lead_generated',
			value: 1,
			tags: [
				`lead_type:${leadType}`,
				propertyId ? `property_id:${propertyId}` : ''
			].filter(Boolean),
			lead_type: leadType,
			property_id: propertyId,
			form_data: formData,
			category: 'conversion'
		});
	}

	/**
	 * Track feature usage
	 */
	function trackFeatureUsage(
		featureName: string,
		context?: Record<string, any>
	): void {
		trackCustomAction(MetricActionName.CalculatorUsage, {
			name: 'feature_used',
			value: 1,
			tags: [`feature:${featureName}`],
			feature_name: featureName,
			category: 'feature',
			...context
		});
	}

	/**
	 * Track Web Vitals manually (usually handled automatically)
	 * Updated to use Custom Vitals API for better tracking
	 */
	function trackWebVital(
		name: string,
		value: number,
		rating: 'good' | 'needs-improvement' | 'poor'
	): void {
		if (!isInitialized()) return;

		try {
			// Use Custom Vitals API for Web Vitals tracking
			addCustomVital(`web_vital_${name}`, {
				duration: value,
				description: `Core Web Vital: ${name}`,
				context: {
					web_vital: name,
					rating,
					value,
					page_url:
						typeof window !== 'undefined' ? window.location.pathname : ''
				}
			});

			// Also track as custom action for additional analytics
			trackCustomAction(MetricActionName.PropertyView, {
				name: `web_vital_${name}`,
				value,
				tags: [`web_vital:${name}`, `rating:${rating}`],
				web_vital: name,
				rating,
				category: 'performance'
			});
		} catch (error) {
			console.warn('Failed to track Web Vital:', error);
		}
	}

	/**
	 * Tracks custom errors
	 */
	function trackError(errorData: DatadogError): void {
		if (!isInitialized()) {
			console.warn('Datadog not initialized, skipping error tracking');
			return;
		}

		try {
			datadogRum.addError(errorData.message, {
				source: errorData.source,
				category: errorData.category,
				stack: errorData.stack,
				fingerprint: errorData.fingerprint,
				startTime: errorData.startTime
			});

			// Complementary error log
			datadogLogs.logger.error(errorData.message, {
				category: errorData.category,
				source: errorData.source,
				stack: errorData.stack
			});
		} catch (error) {
			console.error('Error tracking custom error:', error);
		}
	}

	/**
	 * Sets user information
	 */
	function setUser(user: { id: string; name?: string; email?: string }): void {
		if (!isInitialized()) {
			console.warn('Datadog not initialized, skipping user setting');
			return;
		}

		try {
			// Use individual property setters instead of deprecated setUser
			datadogRum.setUserProperty('id', user.id);
			if (user.name) datadogRum.setUserProperty('name', user.name);
			if (user.email) datadogRum.setUserProperty('email', user.email);

			datadogLogs.setUserProperty('id', user.id);
			if (user.name) datadogLogs.setUserProperty('name', user.name);
			if (user.email) datadogLogs.setUserProperty('email', user.email);
		} catch (error) {
			console.error('Error setting user:', error);
		}
	}

	/**
	 * Update user information (enhanced version)
	 */
	function updateUser(userData: Record<string, any>): void {
		if (!isInitialized()) return;

		try {
			// Use individual property setters instead of deprecated setUser
			Object.keys(userData).forEach(key => {
				datadogRum.setUserProperty(key, userData[key]);
				datadogLogs.setUserProperty(key, userData[key]);
			});
		} catch (error) {
			console.warn('Failed to update Datadog user:', error);
		}
	}

	/**
	 * Adds global context
	 */
	function addContext(key: string, value: any): void {
		if (!isInitialized()) {
			console.warn('Datadog not initialized, skipping context addition');
			return;
		}

		try {
			datadogRum.setGlobalContextProperty(key, value);
			datadogLogs.setGlobalContextProperty(key, value);
		} catch (error) {
			console.error('Error adding context:', error);
		}
	}

	/**
	 * Starts a new view/page
	 */
	function startView(name: string, context: Record<string, any> = {}): void {
		if (!isInitialized()) {
			console.warn('Datadog not initialized, skipping view start');
			return;
		}

		try {
			datadogRum.startView(name);
			datadogLogs.logger.info(`New view started: ${name}`, context);
		} catch (error) {
			console.error('Error starting view:', error);
		}
	}

	/**
	 * Start session replay recording
	 */
	function startSessionReplay(): void {
		if (!isInitialized()) return;

		try {
			datadogRum.startSessionReplayRecording();
		} catch (error) {
			console.warn('Failed to start session replay:', error);
		}
	}

	/**
	 * Stop session replay recording
	 */
	function stopSessionReplay(): void {
		if (!isInitialized()) return;

		try {
			datadogRum.stopSessionReplayRecording();
		} catch (error) {
			console.warn('Failed to stop session replay:', error);
		}
	}

	/**
	 * Start a custom vital measurement
	 */
	function startCustomVital(
		name: string,
		options: { description?: string; context?: Record<string, any> } = {}
	): string | void {
		if (!isCustomVitalsAvailable()) {
			console.warn(
				'Datadog Custom Vitals not available, skipping custom vital start. Make sure experimental features are enabled.'
			);
			return;
		}

		try {
			const vitalOptions: any = {};
			if (options.description) vitalOptions.description = options.description;
			if (options.context) vitalOptions.context = options.context;

			// Return reference for tracking
			return (window.DD_RUM as any).startDurationVital(name, vitalOptions);
		} catch (error) {
			console.error('Error starting custom vital:', error);
		}
	}

	/**
	 * Stop a custom vital measurement
	 */
	function stopCustomVital(nameOrReference: string): void {
		if (!isCustomVitalsAvailable()) {
			console.warn(
				'Datadog Custom Vitals not available, skipping custom vital stop'
			);
			return;
		}

		try {
			(window.DD_RUM as any).stopDurationVital(nameOrReference);
		} catch (error) {
			console.error('Error stopping custom vital:', error);
		}
	}

	/**
	 * Add a custom vital with duration in a single call
	 */
	function addCustomVital(
		name: string,
		options: {
			startTime?: number;
			duration: number;
			description?: string;
			context?: Record<string, any>;
		}
	): void {
		if (!isCustomVitalsAvailable()) {
			console.warn(
				'Datadog Custom Vitals not available, skipping custom vital add'
			);
			return;
		}

		try {
			const vitalOptions: any = {
				duration: options.duration
			};

			if (options.startTime) vitalOptions.startTime = options.startTime;
			if (options.description) vitalOptions.description = options.description;
			if (options.context) vitalOptions.context = options.context;

			(window.DD_RUM as any).addDurationVital(name, vitalOptions);
		} catch (error) {
			console.error('Error adding custom vital:', error);
		}
	}

	/**
	 * Track component rendering performance
	 */
	function trackComponentRender(componentName: string): () => void {
		const startTime = performance.now();
		const vitalRef = startCustomVital(`component_render_${componentName}`, {
			description: `Rendering time for ${componentName} component`,
			context: {
				component_name: componentName,
				page_url: typeof window !== 'undefined' ? window.location.pathname : ''
			}
		});

		// Return a function to stop the measurement
		return () => {
			if (vitalRef) {
				stopCustomVital(vitalRef as string);
			} else {
				// Fallback to manual calculation
				const duration = performance.now() - startTime;
				addCustomVital(`component_render_${componentName}`, {
					startTime,
					duration,
					description: `Rendering time for ${componentName} component`,
					context: {
						component_name: componentName,
						page_url:
							typeof window !== 'undefined' ? window.location.pathname : ''
					}
				});
			}
		};
	}

	/**
	 * Track async operation performance (API calls, data loading, etc.)
	 */
	function trackAsyncOperation(operationName: string): () => void {
		const startTime = performance.now();
		const vitalRef = startCustomVital(`async_operation_${operationName}`, {
			description: `Duration of ${operationName} operation`,
			context: {
				operation_name: operationName,
				page_url: typeof window !== 'undefined' ? window.location.pathname : ''
			}
		});

		return () => {
			if (vitalRef) {
				stopCustomVital(vitalRef as string);
			} else {
				const duration = performance.now() - startTime;
				addCustomVital(`async_operation_${operationName}`, {
					startTime,
					duration,
					description: `Duration of ${operationName} operation`,
					context: {
						operation_name: operationName,
						page_url:
							typeof window !== 'undefined' ? window.location.pathname : ''
					}
				});
			}
		};
	}

	return {
		trackCustomAction,
		trackError,
		setUser,
		addContext,
		startView,
		isInitialized,
		// Portal-specific methods
		trackPropertyEvent,
		trackSearchEvent,
		trackLeadEvent,
		trackFeatureUsage,
		trackWebVital,
		updateUser,
		startSessionReplay,
		stopSessionReplay,
		// Custom Vitals methods
		startCustomVital,
		stopCustomVital,
		addCustomVital,
		// Component performance tracking
		trackComponentRender,
		trackAsyncOperation
	};
}

/**
 * Example: Portal-specific performance tracking composable
 * Demonstrates how to use Custom Vitals in the real estate portal context
 *
 * Usage examples:
 *
 * // In a property listing component:
 * const { trackPropertyListLoad, trackPropertyImageLoad } = usePortalPerformance();
 *
 * // Track property list loading time
 * const stopListLoad = trackPropertyListLoad();
 * // ... fetch properties ...
 * stopListLoad();
 *
 * // Track property image loading
 * trackPropertyImageLoad('property_123');
 */
export function usePortalPerformance() {
	const datadog = useDatadog();

	/**
	 * Track property listing load time
	 */
	function trackPropertyListLoad(filters?: Record<string, any>) {
		return datadog.trackAsyncOperation('property_list_load');
	}

	/**
	 * Track individual property page load
	 */
	function trackPropertyPageLoad(propertyId: string) {
		return datadog.startCustomVital('property_page_load', {
			description: 'Time to load complete property page',
			context: {
				property_id: propertyId,
				page_type: 'property_detail'
			}
		});
	}

	/**
	 * Track property image carousel load time
	 */
	function trackPropertyImageLoad(propertyId: string) {
		return datadog.startCustomVital('property_images_load', {
			description: 'Time to load property image carousel',
			context: {
				property_id: propertyId,
				feature: 'image_gallery'
			}
		});
	}

	/**
	 * Track search functionality performance
	 */
	function trackSearchPerformance(searchQuery: string) {
		return datadog.startCustomVital('property_search', {
			description: 'Time to complete property search',
			context: {
				search_query: searchQuery,
				feature: 'search'
			}
		});
	}

	/**
	 * Track neighborhood data loading
	 */
	function trackNeighborhoodLoad(neighborhoodId: string) {
		return datadog.startCustomVital('neighborhood_data_load', {
			description: 'Time to load neighborhood information',
			context: {
				neighborhood_id: neighborhoodId,
				page_type: 'neighborhood'
			}
		});
	}

	/**
	 * Track calculator performance (financing, etc.)
	 */
	function trackCalculatorPerformance(calculatorType: string) {
		return datadog.startCustomVital(`calculator_${calculatorType}`, {
			description: `Performance of ${calculatorType} calculator`,
			context: {
				calculator_type: calculatorType,
				feature: 'calculator'
			}
		});
	}

	/**
	 * Track form submission performance
	 */
	function trackFormSubmission(formType: string) {
		return datadog.startCustomVital(`form_submission_${formType}`, {
			description: `Time to submit ${formType} form`,
			context: {
				form_type: formType,
				feature: 'lead_generation'
			}
		});
	}

	return {
		trackPropertyListLoad,
		trackPropertyPageLoad,
		trackPropertyImageLoad,
		trackSearchPerformance,
		trackNeighborhoodLoad,
		trackCalculatorPerformance,
		trackFormSubmission
	};
}
